import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BookUser, Loader2, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import ClientForm from "../components/ClientForm";

const EMPTY = { name: "", email: "", phone: "", vat: "", type: "business", address: "" };

function ClientRow({ c, onEdit, onDelete }) {
  return (
    <div data-testid={`client-row-${c.id}`} className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-slate-900 truncate">{c.name}</p>
        <p className="text-xs text-slate-500 truncate">{[c.email, c.phone, c.address].filter(Boolean).join(" · ") || "—"}</p>
      </div>
      <Button size="icon" variant="ghost" data-testid={`client-edit-${c.id}`} onClick={() => onEdit(c)} aria-label="Modifica"><Pencil className="w-4 h-4" /></Button>
      <Button size="icon" variant="ghost" data-testid={`client-delete-${c.id}`} onClick={() => onDelete(c)} aria-label="Elimina"><Trash2 className="w-4 h-4 text-red-600" /></Button>
    </div>
  );
}

function ClientDialog({ editing, setEditing, onSaved }) {
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setEditing((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!editing.name?.trim()) { toast.error("Inserisci il nome del cliente"); return; }
    setSaving(true);
    try {
      const { id, created_at, updated_at, ...body } = editing;
      if (id) await api.put(`/clients/${id}`, body); else await api.post("/clients", body);
      toast.success("Cliente salvato");
      setEditing(null);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-dialog">
        <DialogHeader><DialogTitle>{editing?.id ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle></DialogHeader>
        {editing && <ClientForm c={editing} set={set} />}
        <DialogFooter>
          <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
          <Button data-testid="client-dialog-save" onClick={save} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Clients() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => {
    api.get("/clients", { params: { q } }).then(({ data }) => setRows(data)).catch(() => toast.error("Caricamento non riuscito"));
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const remove = async () => {
    try {
      await api.delete(`/clients/${deleting.id}`);
      toast.success("Cliente eliminato");
      load();
    } catch (e) { toast.error("Eliminazione non riuscita"); }
    setDeleting(null);
  };

  return (
    <div data-testid="clients-page" className="max-w-4xl">
      <div className="flex flex-col sm:flex-row sm:items-end gap-4 mb-6">
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Rubrica Clienti</h1>
          <p className="text-sm text-slate-500 mt-1">I clienti si salvano da soli quando salvi un preventivo. Richiamali in "Dati Cliente" con un clic.</p>
        </div>
        <Button data-testid="client-add-btn" onClick={() => setEditing({ ...EMPTY })} className="gap-1.5"><Plus className="w-4 h-4" /> Nuovo cliente</Button>
      </div>
      <div className="relative mb-4">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, email, P.IVA o città" className="pl-9" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-card">
        {rows === null && <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}
        {rows?.length === 0 && (
          <div data-testid="clients-empty" className="py-12 text-center text-sm text-slate-500">
            <BookUser className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            {q ? "Nessun cliente trovato." : "La rubrica è vuota: salva un preventivo o aggiungi un cliente."}
          </div>
        )}
        {rows?.map((c) => <ClientRow key={c.id} c={c} onEdit={(x) => setEditing({ ...x })} onDelete={setDeleting} />)}
      </div>
      <ClientDialog {...{ editing, setEditing, onSaved: load }} />
      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Il cliente viene tolto dalla rubrica. I preventivi già creati non cambiano.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="client-delete-confirm" onClick={remove}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
